import { AlertCircle, AlertOctagon, CheckCircle, type LucideIcon } from 'lucide-react';
import React from 'react';
import { tv } from 'tailwind-variants';
import { cn } from './utils';
import { getStatusColor, statusVariants, type StatusIntents } from '~/theme/variants';

const bannerStyles = tv({
	extend: statusVariants,
	base: 'mb-6 flex flex-col gap-3 rounded-lg p-4',
	defaultVariants: {
		intent: 'neutral',
		appearance: 'subtleOutline'
	}
});

type BannerIntents = Extract<StatusIntents, 'warning' | 'success' | 'destructive' | 'neutral'>;

const intentIcons: Record<BannerIntents, LucideIcon> = {
	warning: AlertOctagon,
	success: CheckCircle,
	destructive: AlertCircle,
	neutral: AlertCircle
};

export function Banner({
	intent,
	className,
	title,
	message,
	children
}: {
	intent?: BannerIntents;
	className?: string;
	title?: string;
	message: string | string[] | React.ReactNode;
	children?: React.ReactNode;
}) {
	const Icon = intent ? intentIcons[intent] : null;
	const textColor = getStatusColor(intent ?? 'neutral');
	return (
		<div className={bannerStyles({ intent, className: cn(className, !title && 'flex-row') })}>
			<div className={cn('mb-2 flex items-center gap-2', textColor)}>
				{Icon && <Icon className="mt-0.5 size-5 shrink-0" />}
				{title && <h3 className="font-medium">{title}</h3>}
			</div>

			<div>
				{typeof message === 'string' ? (
					<p className={cn('text-sm', textColor)}>{message}</p>
				) : Array.isArray(message) ? (
					<ul className="list-inside list-disc pl-2">
						{message.map((msg, i) => (
							<li key={i} className={cn('text-sm', textColor)}>
								{msg.toString()}
							</li>
						))}
					</ul>
				) : (
					message
				)}
			</div>
			{children}
		</div>
	);
}
